/**
 * 사업장 정보 미완성 사용자 유도 게이트.
 * - 사업장이 0개이거나 **초안(업종·인원 미입력)만 있으면** 강제 모달 노출 → 정보 입력 유도.
 * - 학생/취준생 등 사업장 없는 사용자를 위해 '사업장 등록 안 함' 버튼 제공(닫기).
 *   단, 닫으면 일부 기능 제한 안내 배너를 상단에 상시 표시한다.
 * - 사업장 등록/수정 마법사, 관리자 화면에서는 모달을 띄우지 않는다.
 *
 * ⚠️ 로그인 시 초안 사업장이 자동 생성(provisioning)되므로 '사업장 0개' 조건만 보면
 *    이 게이트가 영영 뜨지 않는다. 그러면 업종·인원이 채워지지 않아 법정 의무·동종 사고·
 *    안전점수·알림 메일이 전부 계산되지 않는다 → 반드시 '초안'까지 미완성으로 취급한다.
 * ⚠️ 또한 초안이 이미 1개 있으므로 Free/Starter(max_sites=1)는 신규 등록(POST /sites)이
 *    403(PLAN_LIMIT_SITES)으로 막힌다 → 초안이 있으면 '신규 등록'이 아니라 그 초안의
 *    '정보 입력(수정)' 화면으로 보낸다.
 */
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { FiBriefcase, FiAlertTriangle } from 'react-icons/fi'
import { sitesApi } from '../../lib/sitesApi'
import type { SiteOut } from '../../types/sites'
import { useAuthStore } from '../../store/authStore'

const DISMISS_KEY = 'heinrich_site_reg_dismissed'

/** 업종·인원이 없으면 '초안' — 백엔드 welcome.site_is_ready 와 같은 기준.
 * OnboardingFollowupGate 도 같은 기준으로 첫 이관 팝업을 보류한다(모달 겹침 방지). */
export function isDraftSite(s: SiteOut): boolean {
  const hasIndustry = !!(s.ksic_name || s.ksic_code || s.ksic_category)
  const workers = Object.values(s.workers || {}).reduce((a, b) => a + (Number(b) || 0), 0)
  return !hasIndustry || workers <= 0
}

export default function SiteRegistrationGate() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const user = useAuthStore((s) => s.user)
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  )

  // 사업장 등록/수정 마법사·관리자 화면에서는 모달 비표시(작업 흐름 방해 방지)
  const onExempt = pathname.startsWith('/dashboard/site/new')
    || /^\/dashboard\/site\/[^/]+\/edit/.test(pathname)
    || pathname.startsWith('/dashboard/admin')

  const { data: sites, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: sitesApi.list,
    enabled: !!user && !user.is_admin,
    staleTime: 1000 * 60,
  })

  const list = sites ?? []
  const ready = list.filter((s) => !isDraftSite(s))
  const draft = list.find(isDraftSite)
  // 정보가 채워진 사업장이 하나라도 있으면 게이트 불필요
  if (isLoading || !user || user.is_admin || ready.length > 0) return null

  // 초안이 있으면 신규 등록이 아니라 그 초안을 채우게 한다(플랜 사업장 수 제한 회피).
  const goSetup = () => navigate(draft ? `/dashboard/site/${draft.id}/edit` : '/dashboard/site/new')

  // 닫은 사용자 → 상단 상시 안내 배너만
  if (dismissed) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/[0.08] px-4 py-2.5 mb-4 text-sm">
        <FiAlertTriangle className="text-amber-500 shrink-0" size={16} />
        <span className="text-ink-muted">
          사업장 정보(업종·근로자)가 없어 <b className="text-ink">법정 의무·동종 사고·안전점수·알림</b>이 계산되지 않습니다.
        </span>
        <button
          onClick={goSetup}
          className="ml-auto shrink-0 text-sm font-semibold text-brand-primary hover:underline"
        >
          지금 입력 →
        </button>
      </div>
    )
  }

  if (onExempt) return null

  // 강제 모달
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface-card border border-surface-elevated/40 shadow-2xl p-7 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/10">
          <FiBriefcase className="text-brand-primary" size={26} />
        </div>
        <h2 className="text-xl font-bold text-ink mb-2">사업장 정보를 입력해주세요</h2>
        <p className="text-sm text-ink-muted leading-relaxed mb-6">
          <b className="text-ink">업종과 근로자 수</b>만 입력하면, 이 사업장에 적용되는
          <b className="text-ink"> 법정 의무·기한, 동종업계 사고, 과태료 노출액</b>을 자동으로 계산해 드립니다.
          안전을 몰라도 됩니다 — <b className="text-ink">2분</b>이면 시작할 수 있어요.
        </p>
        <button
          onClick={goSetup}
          className="w-full mb-2 rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold py-3 transition-colors"
        >
          정보 입력 시작 (2분)
        </button>
        <button
          onClick={() => { localStorage.setItem(DISMISS_KEY, '1'); setDismissed(true) }}
          className="w-full rounded-xl border border-surface-elevated/60 text-ink-muted hover:text-ink hover:border-ink-faint font-medium py-2.5 text-sm transition-colors"
        >
          사업장 등록 안 함 (일부 기능 제한)
        </button>
        <p className="text-[11px] text-ink-faint mt-3">
          학생·취업준비생 등 사업장이 없는 경우 등록하지 않아도 문서 생성 등 일반 기능은 이용할 수 있습니다.
        </p>
      </div>
    </div>
  )
}
